import * as React from 'react';
import { Loader } from 'semantic-ui-react';
import { tl } from 'common/services/i18nService';
import RedTrashIcon from '../../../icons/RedTrashIcon';
import StepperPlusIcon from '../../../icons/StepperPlusIcon';
import StepperMinusIcon from '../../../icons/StepperMinusIcon';
import GreaterThanOrEqualIcon from '../../../icons/GreaterThanOrEqualIcon';
import { ThresholdLevel } from '../types';
import {
    addThreshold,
    removeThreshold,
    updateMinValue,
    updateRewardValue,
    canAddThreshold,
    createDefaultThreshold,
    getIntervalMinValue,
    sanitizeThresholds,
} from '../thresholdScoringService';
import styles from './ThresholdScoringEditor.scss';

interface ThresholdScoringEditorProps {
    thresholds: ThresholdLevel[];
    maxScore: number;
    minScore: number;
    step: number;
    nrOfDecimals: number;
    contentItemId?: number;
    onSave: (thresholds: ThresholdLevel[]) => Promise<void> | void;
    onClose: () => void;
}

const ThresholdScoringEditor: React.FC<ThresholdScoringEditorProps> = ({
    thresholds: initialThresholds,
    maxScore,
    minScore,
    step,
    nrOfDecimals,
    contentItemId,
    onSave,
    onClose,
}) => {
    const [thresholds, setThresholds] = React.useState<ThresholdLevel[]>(() => {
        if (initialThresholds.length > 0) {
            return sanitizeThresholds(initialThresholds, maxScore, minScore, nrOfDecimals);
        }
        return createDefaultThreshold(minScore, maxScore, nrOfDecimals);
    });
    const [hasChanges, setHasChanges] = React.useState(false);
    const [showUnsavedWarning, setShowUnsavedWarning] = React.useState(false);
    const [isSaving, setIsSaving] = React.useState(false);

    const handleMinValueChange = (levelIndex: number, newValue: number) => {
        const updated = updateMinValue(thresholds, levelIndex, newValue, step, nrOfDecimals);
        setThresholds(updated);
        setHasChanges(true);
    };

    const handleRewardValueChange = (levelId: string, newValue: number) => {
        const updated = updateRewardValue(thresholds, levelId, newValue, nrOfDecimals);
        setThresholds(updated);
        setHasChanges(true);
    };

    const handleAddThreshold = () => {
        if (canAddThreshold(thresholds, maxScore, step)) {
            const updated = addThreshold(thresholds, maxScore, step, nrOfDecimals);
            setThresholds(updated);
            setHasChanges(true);
        }
    };

    const handleRemoveThreshold = (level: ThresholdLevel) => {
        const updated = removeThreshold(thresholds, level, maxScore, nrOfDecimals);
        setThresholds(updated);
        setHasChanges(true);
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const sanitized = sanitizeThresholds(thresholds, maxScore, minScore, nrOfDecimals);
            setThresholds(sanitized);
            await onSave(sanitized);
        } finally {
            setHasChanges(false);
            setIsSaving(false);
        }
    };

    const handleCloseAttempt = () => {
        if (hasChanges) {
            setShowUnsavedWarning(true);
        } else {
            onClose();
        }
    };

    const handleDiscardAndExit = () => {
        setShowUnsavedWarning(false);
        onClose();
    };

    const handleSaveAndExit = async () => {
        setShowUnsavedWarning(false);
        setIsSaving(true);
        try {
            const sanitized = sanitizeThresholds(thresholds, maxScore, minScore, nrOfDecimals);
            await onSave(sanitized);
        } finally {
            setHasChanges(false);
            setIsSaving(false);
            onClose();
        }
    };

    const handleDismissWarning = () => {
        setShowUnsavedWarning(false);
    };

    const addEnabled = canAddThreshold(thresholds, maxScore, step);
    const sorted = [...thresholds].sort((a, b) => a.levelIndex - b.levelIndex);
    const reversed = [...sorted].reverse();
    const maxLevelIndex = thresholds.length;
    const previewUrl = contentItemId
        ? `/player?mode=question&context=preview&contentItemId=${contentItemId}`
        : '';

    return (
        <div className={styles.thresholdScoringOverlay}>
            <div className={styles.topBar}>
                <span className={styles.overlayTitle}>
                    {tl('author.thresholdScoring.overlayTitle')}
                </span>
                <button
                    className={styles.closeButton}
                    onClick={handleCloseAttempt}
                    type="button"
                >
                    <span className={styles.closeIcon}>&times;</span>
                    {tl('common.close')}
                </button>
            </div>
            <div className={styles.splitContainer}>
                <div className={styles.questionPreviewPane}>
                    {previewUrl && (
                        <iframe
                            className={styles.questionIframe}
                            src={previewUrl}
                            title={tl('author.thresholdScoring.questionPreview')}
                        />
                    )}
                </div>

                <div className={styles.thresholdPane}>
                    <div className={styles.thresholdScoringEditor}>
                        <div className={styles.parametersSection}>
                            <h3 className={styles.parametersSectionTitle}>
                                {tl('author.thresholdScoring.parametersTitle')}
                            </h3>
                            <p className={styles.parameterLine}>
                                {tl('author.thresholdScoring.defaultMax')}
                                <strong>{Number.parseFloat(String(maxScore)).toFixed(nrOfDecimals)}</strong>
                            </p>
                            <p className={styles.parameterLine}>
                                {tl('author.thresholdScoring.defaultMin')}
                                <strong>{Number.parseFloat(String(maxScore)).toFixed(nrOfDecimals)}</strong>
                            </p>
                        </div>

                        <div className={styles.thresholdList}>
                            <div className={styles.columnsHeader}>
                                <div className={styles.rangeHeader}>
                                    {tl('author.thresholdScoring.rangeOfCorrectResponses')}
                                </div>
                                <div className={styles.rewardHeader}>
                                    {tl('author.thresholdScoring.rewardValue')}
                                </div>
                            </div>

                            {reversed.map((level) => (
                                <ThresholdRow
                                    key={level.id}
                                    level={level}
                                    nrOfDecimals={nrOfDecimals}
                                    step={step}
                                    minScore={minScore}
                                    thresholds={thresholds}
                                    maxLevelIndex={maxLevelIndex}
                                    onMinValueChange={handleMinValueChange}
                                    onRewardValueChange={handleRewardValueChange}
                                    onRemove={handleRemoveThreshold}
                                />
                            ))}
                        </div>

                        <button
                            className={`${styles.addThresholdButton} ${!addEnabled ? styles.disabled : ''}`}
                            onClick={addEnabled ? handleAddThreshold : undefined}
                            disabled={!addEnabled}
                            type="button"
                        >
                            <StepperPlusIcon color="#272727" />
                            {tl('author.thresholdScoring.addThresholdBtn')}
                        </button>
                    </div>
                </div>
            </div>
            <div className={styles.footer}>
                <button
                    className={styles.saveButton}
                    onClick={handleSave}
                    disabled={isSaving || !hasChanges}
                    type="button"
                >
                    {tl('author.thresholdScoring.saveThreshold')}
                </button>
            </div>

            {isSaving && (
                <div className={styles.savingOverlay}>
                    <Loader active />
                </div>
            )}

            {showUnsavedWarning && (
                <div className={styles.unsavedChangesOverlay}>
                    <div className={styles.unsavedChangesDialog}>
                        <div className={styles.dialogTitle}>
                            <span>{tl('author.thresholdScoring.unsavedChangesTitle')}</span>
                            <button
                                className={styles.dialogCloseButton}
                                onClick={handleDismissWarning}
                                type="button"
                                aria-label={tl('common.close')}
                            >
                                &times;
                            </button>
                        </div>
                        <div className={styles.dialogMessage}>
                            {tl('author.thresholdScoring.unsavedChangesMessage')}
                        </div>
                        <div className={styles.dialogActions}>
                            <button
                                className={styles.discardButton}
                                onClick={handleDiscardAndExit}
                                type="button"
                            >
                                {tl('author.thresholdScoring.discardChanges')}
                            </button>
                            <button
                                className={styles.saveAndExitButton}
                                onClick={handleSaveAndExit}
                                type="button"
                            >
                                {tl('author.thresholdScoring.saveAndExit')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

interface ThresholdRowProps {
    level: ThresholdLevel;
    nrOfDecimals: number;
    step: number;
    minScore: number;
    thresholds: ThresholdLevel[];
    maxLevelIndex: number;
    onMinValueChange: (levelIndex: number, newValue: number) => void;
    onRewardValueChange: (levelId: string, newValue: number) => void;
    onRemove: (level: ThresholdLevel) => void;
}

const ThresholdRow: React.FC<ThresholdRowProps> = ({
    level,
    nrOfDecimals,
    step,
    minScore,
    thresholds,
    maxLevelIndex,
    onMinValueChange,
    onRewardValueChange,
    onRemove,
}) => {
    // AC1.8: Bottom level (levelIndex=1) — only show decrement when minValue > minScore
    const isMinEditable = level.levelIndex > 1;
    const isBottomWithRoom = level.levelIndex === 1 && Number(level.minValue) > minScore;
    // Highest level cannot be deleted (always need one covering up to maxScore)
    const isDeletable = level.levelIndex < maxLevelIndex && thresholds.length > 1;
    const intervalMin = level.levelIndex === 1 ? minScore : getIntervalMinValue(thresholds, level.levelIndex, step);
    const intervalMax = Number(level.maxValue);

    const handleMinDecrement = () => {
        const newValue = Number(level.minValue) - step;
        if (newValue >= intervalMin) {
            onMinValueChange(level.levelIndex, newValue);
        }
    };

    const handleMinIncrement = () => {
        const newValue = Number(level.minValue) + step;
        if (newValue <= intervalMax) {
            onMinValueChange(level.levelIndex, newValue);
        }
    };

    const handleRewardDecrement = () => {
        const newValue = Number(level.rewardValue) - step;
        if (newValue >= 0) {
            onRewardValueChange(level.id, newValue);
        }
    };

    const handleRewardIncrement = () => {
        const newValue = Number(level.rewardValue) + step;
        onRewardValueChange(level.id, newValue);
    };

    return (
        <div className={styles.thresholdRow}>
            <div className={styles.rangeSection}>
                <span className={styles.valueBox}>
                    {Number(level.maxValue).toFixed(nrOfDecimals)}
                </span>
                <span className={styles.separator}><GreaterThanOrEqualIcon /></span>
                {isMinEditable ? (
                    <div className={styles.stepperGroup}>
                        <button
                            className={styles.stepperButton}
                            onClick={handleMinDecrement}
                            disabled={Number(level.minValue) - step < intervalMin}
                            type="button"
                            aria-label={tl('author.thresholdScoring.decreaseFromValue')}
                        >
                            <StepperMinusIcon />
                        </button>
                        <span className={styles.valueBox}>
                            {Number(level.minValue).toFixed(nrOfDecimals)}
                        </span>
                        <button
                            className={styles.stepperButton}
                            onClick={handleMinIncrement}
                            disabled={Number(level.minValue) + step > intervalMax}
                            type="button"
                            aria-label={tl('author.thresholdScoring.increaseFromValue')}
                        >
                            <StepperPlusIcon />
                        </button>
                    </div>
                ) : (
                    <div className={styles.stepperGroup}>
                        {isBottomWithRoom ? (
                            <button
                                className={styles.stepperButton}
                                onClick={handleMinDecrement}
                                disabled={Number(level.minValue) - step < minScore}
                                type="button"
                                aria-label={tl('author.thresholdScoring.decreaseFromValue')}
                            >
                                <StepperMinusIcon />
                            </button>
                        ) : (
                            <span className={styles.stepperButton} style={{ visibility: 'hidden' }} />
                        )}
                        <span className={styles.valueBox}>
                            {Number(level.minValue).toFixed(nrOfDecimals)}
                        </span>
                        <button
                            className={styles.stepperButton}
                            onClick={handleMinIncrement}
                            disabled={Number(level.minValue) + step > intervalMax}
                            type="button"
                            aria-label={tl('author.thresholdScoring.increaseFromValue')}
                        >
                            <StepperPlusIcon />
                        </button>
                    </div>
                )}
            </div>

            <div className={styles.rewardSection}>
                <div className={styles.stepperGroup}>
                    <button
                        className={styles.stepperButton}
                        onClick={handleRewardDecrement}
                        disabled={Number(level.rewardValue) - step < 0}
                        type="button"
                        aria-label={tl('author.thresholdScoring.decreaseMarksAwarded')}
                    >
                        <StepperMinusIcon />
                    </button>
                    <span className={styles.valueBox}>
                        {Number(level.rewardValue).toFixed(nrOfDecimals)}
                    </span>
                    <button
                        className={styles.stepperButton}
                        onClick={handleRewardIncrement}
                        type="button"
                        aria-label={tl('author.thresholdScoring.increaseMarksAwarded')}
                    >
                        <StepperPlusIcon />
                    </button>
                </div>
            </div>

            <div className={styles.removeAction}>
                {isDeletable && (
                    <button
                        className={styles.removeButton}
                        onClick={() => onRemove(level)}
                        type="button"
                        aria-label={tl('author.thresholdScoring.removeThreshold')}
                    >
                        <RedTrashIcon />
                    </button>
                )}
            </div>
        </div>
    );
};

export default ThresholdScoringEditor;
